'use client'
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from "react-redux";
import PhoneInput from "react-phone-input-2";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";
import { TbCurrentLocation, LuChevronsLeftRight, PiCityBold } from '@assets/Icons';
import ImagesData from '@assets/ImagesData';
import CommonSlider from '@shared/slick/CommonSlider';
import Accordion from '@shared/accordian/Accordion';
import services from '@api/services';
import { mainUrl } from "@api/client/api";
import SearchBar from '@components/searchBar/SearchBar';
import { setFilter, setSearch, setCityDetail } from "@store/slices/propertySlice";
import { TypeAnimation } from "react-type-animation";
import { GoClockFill } from "react-icons/go";
import { MdBusinessCenter } from "react-icons/md";
import { LuHeartHandshake } from "react-icons/lu";
import { MdFamilyRestroom } from "react-icons/md";
import { FaRegClock } from "react-icons/fa";

const popularCities = ["Lucknow", "Ayodhya", "Agra", "Bareilly", "Varanasi", "Prayagraj", "Vrindavan", "Chitrakoot", "Kanpur", "Sitapur"];
const priority = ["Lucknow", "Agra", "Bareilly", "Prayagraj"];

const travelOptions = [
  {
    city: "Hourly-Stays",
    name: "Time at your command. RROOMS offers hourly stays – your cozy escape, your way.",
    id: 8,
    img: ImagesData?.ImgHourly,
  },

  {
    city: "Business Travel",
    name: "Business travel made easy. RROOMS offers exclusive corporate discounts.",
    id: 2,
    img: ImagesData?.ImgBusinessTravel,
  },
  {
    city: "Couple Friendly",
    name: "Love without compromise. RROOMS welcomes couples to a secure and romantic retreat.",
    id: 7,
    img: ImagesData?.ImgCoupleFriendly,
  },
  {
    city: "Family Vacation",
    name: "Safe, warm, and family-approved. RROOMS, your perfect base for a memorable family getaway.",
    id: 3,
    img: ImagesData?.ImgFamilyVacation,
  },
  {
    city: "Group Travel",
    name: "Group adventures, individual comforts. Your collective journey finds a cozy refuge here.",
    id: 5,
    img: ImagesData?.ImgGroupTravel,
  },
  {
    city: "Solo Traveler",
    name: "Solo adventures, pocket-friendly stays. RROOMS is your trusted companion for the solo explorer.",
    id: 6,
    img: ImagesData?.ImgSoloTravel,
  },
]

const testimonial = [
  {
    name: 'Priya Verma',
    designation: 'Reliance Digital',
    content: 'I really loved how transparent the pricing was. No hidden charges at all, and the rooms were exactly as shown in the photos.',
    img: ImagesData?.ImgTestimonial
  },
  {
    name: 'Sneha Kapoor',
    designation: 'TCS',
    content: 'The hotel details and amenities were clearly listed, and everything matched perfectly on arrival. Check-in was super easy.',
    img: ImagesData?.ImgTestimonial
  },
  {
    name: 'Vikram Singh',
    designation: 'The Curve',
    content: 'I travel a lot, and this app makes finding hotels by location so simple. It saved me a lot of time while planning my trip.',
    img: ImagesData?.ImgTestimonial
  },
  {
    name: 'Ankit Mehta',
    designation: 'IBM',
    content: 'I needed a last-minute booking for my business trip, and this app gave me the best options within seconds. The payment was smooth and secure.',
    img: ImagesData?.ImgTestimonial
  }
]

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [cities, setCities] = useState([]);
  const [locationData, setLocation] = useState("");

  useEffect(() => {
    const body = document.body;
    if (location.pathname == "/") {
      body.classList.add("home");
      sessionStorage.removeItem("cityName");
      dispatch(setCityDetail(""))
    }
    else { body.classList.remove("home") }
    return () => body.classList.remove("home");
  }, [location.pathname]);

  const validatePhone = () => {
    if (!phone) { return "Enter Mobile Number*" }
    if (!/^[0-9]+$/.test(phone)) { return "Enter Correct Mobile Number*" }
    if (phone.startsWith("91")) {
      const localNumber = phone.replace("91", "");
      if (localNumber.length != 10) { return "Enter Correct Mobile Number*" }
    }
    return "";
  };

  const submitMobileNumber = async (e) => {
    e.preventDefault();
    const validationError = validatePhone();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    try {
      const res = await services.auth.getAppService({ mobile: phone, });
      if (res.status == 200) {
        setPhone("");
        toast.success("Url sent successfully!", { autoClose: 1000, pauseOnHover: true, position: "top-right" });
      }
    } catch (error) { console.log("Error in submitMobileNumber function.") }
  };

  const fetchCities = async () => {
    try {
      const res = await services.globalService.getCities()
      if (res.status == 200) { setCities(res?.data?.data) }
    }
    catch (error) { console.log("Error in fetchCities function.") }
  }

  useEffect(() => { fetchCities(); }, [])

  const showPosition = (position) => { setLocation(position?.coords) };

  const getLocation = () => {
    if (navigator.geolocation) { navigator.geolocation.getCurrentPosition(showPosition) }
    else { alert("Location is not supported by this browser.") }
  };

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "first_visit" });
    getLocation();
  }, []);

  const filterCities = useMemo(() => {
    const filtered = cities?.filter((item) => popularCities?.includes(item.name));
    return filtered.sort((a, b) => {
      const indexA = priority.indexOf(a.name);
      const indexB = priority.indexOf(b.name);
      if (indexA != -1 && indexB != -1) { return indexA - indexB; }
      else if (indexA != -1) { return -1; }
      else if (indexB != -1) { return 1; }
      else { return a.name.localeCompare(b.name); }
    });
  }, [cities]);

  const goToPropertyListing = useCallback((e, item) => {
    e.preventDefault();
    dispatch(setCityDetail(item))
    sessionStorage.setItem("cityName", item?.name);
    if (item.name != 'AllCities') {
      dispatch(setSearch(item?.name));
      dispatch(setFilter({ cityId: item.id, latlongFilter: { lat: null, long: null } }));
    }
    if (item?.name == "Lucknow") {
      // navigate("/hotels-in-lucknow");
      navigate("/best-hourly-hotels-in-lucknow");
    }
    else if (item?.name == "AllCities") { navigate("/best-hourly-hotels-in-India") }
    else {
      // navigate("/hotel-listing");
      navigate(`/best-hourly-hotels-in-${item?.name?.trim().replace(/\s+/g, '-')}`);
    }
  }, [dispatch, navigate])

  const nearByClick = async () => {
    navigator.geolocation.getCurrentPosition(async (pos) => {
      // const { latitude, longitude, accuracy } = pos.coords;
      const { latitude, longitude } = locationData;
      // if (accuracy > 4000) {
      //   alert("Location not accurate. Please turn on GPS or select city manually.");
      //   return;
      // }
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
      const data = await res.json();

      console.log("data - ", data);

      const city = data?.address?.city || data?.address?.town || data?.address?.suburb || data?.address?.county;
      if (!city) {
        alert("Unable to detect city. Please select manually.");
        return;
      }
      sessionStorage.setItem("cityName", city);
      const selectedCity = cities?.find(v => v?.name?.toLowerCase() == city?.toLowerCase());
      if (selectedCity) { dispatch(setCityDetail(selectedCity)); }
      dispatch(setFilter({ latlongFilter: { lat: latitude, long: longitude } }));
      navigate(`/best-hourly-hotels-in-${city}`);
    });
    // const { latitude, longitude } = locationData;
    // dispatch(setFilter({ latlongFilter: { lat: latitude, long: longitude } }));
    // navigate(`/best-hourly-hotels-in-${city}`);
  };

  // const nearByClick = async () => {
  //   navigator.geolocation.getCurrentPosition(async (pos) => {
  //     const { latitude, longitude } = pos.coords.accuracy;
  //     const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
  //     const data = await res.json();
  //     const city = data?.city || data?.locality || "";
  //     sessionStorage.setItem("cityName", city);
  //     const selectedCity = cities?.find((v) => v?.name?.toLowerCase() == city?.toLowerCase());
  //     if (selectedCity) { dispatch(setCityDetail(selectedCity)) }
  //     dispatch(setFilter({ latlongFilter: { lat: latitude, long: longitude } }));
  //     navigate(`/best-hourly-hotels-in-${city}`);
  //   }, (error) => { console.error("Location error:") }, { enableHighAccuracy: true, timeout: 10000 });
  // };

  const nearByClickdd = async () => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (accuracy > 5000) {
          alert("Location not accurate. Please turn on GPS or select city manually.");
          return;
        }
        const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
        const data = await res.json();
        const city = data?.city || data?.principalSubdivision || data?.locality || "";
        if (!city) {
          alert("Unable to detect city. Please select manually.");
          return;
        }
        sessionStorage.setItem("cityName", city);
        const selectedCity = cities?.find(v => v?.name?.toLowerCase() == city.toLowerCase());
        if (selectedCity) { dispatch(setCityDetail(selectedCity)); }
        dispatch(setFilter({ latlongFilter: { lat: latitude, long: longitude } }));
        navigate(`/best-hourly-hotels-in-${city}`);
      },
      (error) => { alert("Location permission denied or unavailable."); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const goToListing = (e, item) => {
    e.preventDefault();
    dispatch(setSearch(""));
    dispatch(setFilter({ travellerChoice: [item.id] }));
    navigate("/hotel-listing");
  };

  return (
    <>
      <div className="app-search"><SearchBar /></div>
      <section className="banner-section bg-control">
        <video autoPlay muted loop playsInline ><source src={ImagesData?.Video1} type="video/mp4" /></video>
        <div className="layer">
          <div className="container">
            <div className="text-white text-center mb-5">
              <h1 className="mb-3">
                RROOMS -
                <span className='ml-1'>
                  <TypeAnimation
                    sequence={[
                      "Stay Smart Pay by the Hour", 3000,
                      "Unlock India's Hotel Diversity", 3000,
                      "Explore By Cities", 3000,
                      "Exclusive offer for you", 3000,
                    ]}
                    speed={50}
                    repeat={Infinity}
                  />
                </span>
              </h1>
              <p className='fs-5'>Find the perfect room for every occasion. Book budget, premium, and luxury stays.</p>
            </div>
            <SearchBar locationData={locationData} nearByClick={nearByClick} />
          </div>
        </div>
      </section>
      <section className='app-banner'></section>
      <section className='city-section'>
        <section className='hourly-section'>
          <div className='container'>
            <div className='content'>
              <div className='row'>
                <div className='col-lg-7'>
                  <div className='col-left'>
                    <h2> Stay Smart <span>Pay by the Hour</span></h2>
                    <p>Skip the full-day charges. Choose hourly rates for maximum savings.</p>
                  </div>
                </div>
                <div className='col-lg-5'>
                  <div className='col-right'>
                    <div className='flex'>
                      <div className='left'>
                        <FaRegClock className='icon' />
                      </div>
                      <div className='right'>
                        <h5 className='text-uppercase'>3 | 6 | 9 Hour Stays . Anytime . Anywhere</h5>
                        <p>Why pay for the whole day when you need just a few hours?</p>
                      </div>
                    </div>
                    <div className='text-left'>
                      <button type='button' className='btn btn-primary' onClick={(e) => {
                        nearByClick();
                        goToListing(e, { id: 8, city: "Hourly-Stays" })
                      }}>Explore Now</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className='container'>
          <h4 className='fw-semibold pb-4'>Explore By Cities</h4>
          <div className='flex text-center no-scrollbar'>
            <div className='column cursor-pointer' onClick={nearByClick}>
              <span className='all-center'><TbCurrentLocation size={40} /></span>
              <h6 className='text-gray fw-semibold mt-1'>Nearby</h6>
            </div>
            {cities?.length > 0 ?
              filterCities?.map((item) => {
                let cityImage = `${mainUrl}${item.city_img}`;
                return (
                  <div
                    className='column cursor-pointer' key={item?.id}
                    onClick={(e) => goToPropertyListing(e, item)}>
                    <img src={cityImage} className='d-block' title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    <h6 className='fw-semibold mt-1'>{item?.name}</h6>
                  </div>
                )
              }) :
              <>
                {Array?.from({ length: 11 })?.map((_, i) => (
                  <div className='column' key={i}>
                    <div className='skl-city-card'>
                      <div className='top skeleton'></div>
                      <div className='bottom skeleton'></div>
                    </div>
                  </div>
                ))}
              </>
            }
            <div className='column cursor-pointer' onClick={(e) => goToPropertyListing(e, { name: 'AllCities' })}>
              <span className='all-center bg-dark'><PiCityBold size={40} /></span>
              <h6 className='text-gray fw-semibold mt-1'>All Cities</h6>
            </div>
          </div>
        </div>
      </section>
      <section className='app-travel-categories'>
        <div className='content'>
          <div className='column one' onClick={(e) => goToListing(e, { id: 1 })}>
            {/* <span><img src={ImagesData?.IconShort} className='one' title='' alt='' /></span> */}
            <span><GoClockFill size={32} /></span>
            <h6>Hourly Stay</h6>
          </div>
          <div className='column two' onClick={(e) => goToListing(e, { id: 2 })}>
            {/* <span><img src={ImagesData?.IconBusiness} title='' alt='' /></span> */}
            <span><MdBusinessCenter size={38} /></span>
            <h6>Business Travel</h6>
          </div>
          <div className='column three' onClick={(e) => goToListing(e, { id: 7 })}>
            {/* <span><img src={ImagesData?.IconCouple} title='' alt='' /></span> */}
            <span><LuHeartHandshake size={36} /></span>
            <h6>Couple Friendly</h6>
          </div>
          <div className='column four' onClick={(e) => goToListing(e, { id: 3 })}>
            {/* <span><img src={ImagesData?.IconFamily} title='' alt='' /></span> */}
            <span><MdFamilyRestroom size={34} /></span>
            <h6>Family Vacation</h6>
          </div>
        </div>
      </section>
      <section className='offer-banner-section'>
        <div className='container'>
          <div className='content bg-white rounded-2 p-3'>
            <div className='row justify-content-between'>
              <div className='col col-lg-7 col-md-12'>
                <div className='left'>
                  <h6 className='fw-semibold text-gray'>Exclusive offer for you</h6>
                  <div className='v-center mt-4 mb-4'>
                    <img src={ImagesData?.IconPhone} className='d-block mr-2' title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    <div>
                      <h2 className='lg line-height-1 pb-2'>Download App Now !</h2>
                      <p className='fs-6'>
                        <span className='fw-bold'>10%</span> Additional
                        <span className='fw-bold'> OFF</span> with Wallet Payment + App Exclusive Offers,
                        <span className='fw-bold'> Upto 30% OFF</span>
                      </p>
                    </div>
                  </div>
                  <form className='flex' onSubmit={submitMobileNumber}>
                    <PhoneInput country={"in"} value={phone} onChange={setPhone} placeholder="Enter phone number" inputStyle={{ width: "100%" }} />
                    <button type="submit" className="btn btn-primary br-tl-0 br-bl-0">Get App link</button>
                  </form>
                  {error && (<div className="text-danger"><span style={{ fontSize: ".75rem" }}>{error}</span></div>)}
                  <p className='fs-6 mt-1'>Refer Friend to <span className='text-primary fw-bold'>Earn Wallet Points</span></p>
                </div>
              </div>
              <div className='col-lg-4 col-md-12'>
                <div className='row align-items-center'>
                  <div className='col-lg-6 col-sm-6 col-xm-6 text-center'>
                    <h6 className='fw-semibold pt-2 pb-2'>Download the App</h6>
                    <a href="https://apps.apple.com/in/app/rrooms-hotel-booking-app/id6477883837" target="_blank"><img src={ImagesData?.AppStore} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' /></a>
                    <a href="https://play.google.com/store/apps/details?id=com.rrooms" target="_blank"><img src={ImagesData?.PlayStore} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' /></a>
                  </div>
                  <div className='col-lg-6 col-sm-6 col-xm-6'>
                    <img src={ImagesData?.Scan2} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='section travel-categories-section text-center'>
        <div className='container'>
          <CommonSlider settings={{ slidesToShow: 5, autoplay: true }} slides={{ 1400: 4, 1024: 3, 767: 2, 480: 2 }}>
            {travelOptions?.map((item, key) => {
              return (
                <div className='column' key={key} onClick={(e) => goToListing(e, item)}>
                  <div className='img'>
                    <img src={item?.img} className='w-100' title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    <h5 className='text-white'>{item?.city}</h5>
                  </div>
                  <div className='text'>
                    <p className='fs-7'>{item?.name}</p>
                  </div>
                </div>
              )
            })}
          </CommonSlider>
        </div>
      </section>
      <section className='choose-section'>
        <div className='container'>
          <h2 className='lg'>Why you should choose us?</h2>
          <p className='fs-5 pt-3 pb-4'>Imagine waking up to a place where luxury meets nature, where every moment feels like a cherished memory in the making. Welcome to RRooms, where dreams become reality and the extraordinary is simply our everyday.</p>
          <div className='row'>
            <div className='col-lg-7 col-md-12 col-sm-12 col-xm-12'>
              <div className='left'>
                <img src={ImagesData?.ImgChoose} className='rounded-1 w-100' title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
              </div>
            </div>
            <div className='col-lg-5 col-md-12 col-sm-12 col-xm-12'>
              <div className='right pl-5'>
                <ul className='pb-1'>
                  <li className='flex align-items-center'>
                    <span className='mr-1'>
                      <img src={ImagesData?.IconChoose1} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    </span>
                    <h4 className='fw-semibold pb-3'>Exceptional Location</h4>
                  </li>
                  <li className='flex align-items-center'>
                    <span className='mr-1'>
                      <img src={ImagesData?.IconChoose2} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    </span>
                    <h4 className='fw-semibold pb-3'>Affordable Accommodations</h4>
                  </li>
                  <li className='flex align-items-center'>
                    <span className='mr-1'>
                      <img src={ImagesData?.IconChoose3} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    </span>
                    <h4 className='fw-semibold pb-3'>Amenities and Facilities</h4>
                  </li>
                  <li className='flex align-items-center'>
                    <span className='mr-1'>
                      <img src={ImagesData?.IconChoose4} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    </span>
                    <h4 className='fw-semibold pb-3'>Exclusive Packages and Offers</h4>
                  </li>
                  <li className='flex align-items-center'>
                    <span className='mr-1'>
                      <img src={ImagesData?.IconChoose5} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                    </span>
                    <h4 className='fw-semibold pb-3'>Our Commitment to Cleanliness and Safety</h4>
                  </li>
                </ul>
                <h2>Don&apos;t think Book in a blink!</h2>
                <h4 className='pt-1 pb-5'>
                  <span className='text-light fw-light'>book your first stay & get upto </span>
                  30% discount
                </h4>
                <button className='btn btn-primary' onClick={(e) => goToPropertyListing(e, { name: 'AllCities' })}>Book Now</button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='feature-section'>
        <div className='container'>
          <div className='d-flex'>
            <div className='column'>
              <img src={ImagesData?.IconFeature1} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
              <h6 className='text-right'>
                Best deal &<br /> Prices <span className='fw-light'>on Hotels</span>
              </h6>
            </div>
            <div className='column' style={{ backgroundColor: '#FFE4D0' }}>
              <img src={ImagesData?.IconFeature2} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
              <h6 className='text-right'>
                24x7<br /> <span className='fw-light'>Customer Support</span>
              </h6>
            </div>
            <div className='column' style={{ backgroundColor: '#CAEADA' }}>
              <img src={ImagesData?.IconFeature3} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
              <h6 className='text-right'>
                Largest selection<br /> <span className='fw-light'>of Hotels</span>
              </h6>
            </div>
            <div className='column' style={{ backgroundColor: '#FED9DA' }}>
              <img src={ImagesData?.IconFeature4} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
              <h6 className='text-right'>
                Pay at Hotel<br /> <span className='fw-light'>Option available</span>
              </h6>
            </div>
            <div className='column' style={{ backgroundColor: '#E7F0FE' }}>
              <img src={ImagesData?.IconFeature5} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
              <h6 className='text-right'>
                <span className='fw-light'>Plan together with</span><br /> Shared wishlists
              </h6>
            </div>

          </div>
        </div>
      </section>
      <section className='section-lg testimonial-section'>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-4 left col-md-12 col-sm-12 col-xm-12'>
              <div className='row'>
                <div className='col-lg-12 col-md-6'>
                  <h6 className='v-center text-uppercase text-primary pb-3'><LuChevronsLeftRight size={24} className='mr-1' />Testimonials</h6>
                  <h2 className='text-uppercase lg pb-10'><span className='d-block'>What Customer</span> Say About <span className='text-primary'>R</span>ROOMS</h2>
                </div>
                <div className='rights col-lg-12 col-md-6'>
                  <img src={ImagesData?.SatisfiedCustomer} className='pt-10' title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                </div>
              </div>
            </div>
            <div className='col-lg-8 col-md-12 col-sm-12 col-xm-12'>
              <div className='right'>
                <CommonSlider settings={{ slidesToShow: 1, centerMode: true, centerPadding: "15%" }}>
                  {/* <CommonSlider settings={{ slidesToShow: 1 }}> */}
                  {
                    testimonial.map((item, i) => {
                      return (
                        <div className='column' key={i}>
                          <div className='flex align-items-center justify-content-between mb-4'>
                            <img src={ImagesData?.IconQuote} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                            <img src={ImagesData?.IconRatingStar} alt='Best Hourly Hotels in India' />
                          </div>
                          <div>
                            <h4 className='fw-semibold'>{item?.content}</h4>
                          </div>
                          <div className='secondary-divider mt-5 mb-4'></div>
                          <div className='profile flex'>
                            <div className='mr-2'>
                              <img src={item?.img} title='Best Hourly Hotels in India' alt='Best Hourly Hotels in India' />
                            </div>
                            <div>
                              <h4>{item?.name}</h4>
                              <p className='fs-7 text-light'>{item?.designation}</p>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  }
                </CommonSlider>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='mt-2 mb-3'>
        <div className='primary-divider'></div>
      </section>
      <section className='section faq-section'>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-4 col-md-12'>
              <div className='heading'>
                <h6 className='text-uppercase text-primary v-center'>
                  <LuChevronsLeftRight size={24} className='mr-1' />
                  Frequently Asked Questions
                </h6>
                <h2 className='text-center mt-3 mb-3'>FAQ</h2>
                <h6 className='text-uppercase v-center text-gray'>
                  <LuChevronsLeftRight size={24} className='mr-1' />
                  RROOMS Booking FAQs
                </h6>
              </div>
            </div>
            <div className='col-lg-8 col-md-12'>
              <div className='faqs'>
                <Accordion title="How to book a hotel online with RROOMS?">
                  <p>
                    Booking a hotel with RROOMS is simple and hassle-free. Just enter your destination, travel dates and number of guests, then browse through the list of available hotels. Once you find the right property, select your preferred room type, add your details, and complete the payment. You will instantly receive a confirmation along with booking details on your email and phone.
                  </p>
                </Accordion>
                <Accordion title="How to find the cheapest hotel deals in any city?">
                  <p>
                    RROOMS helps you find the most affordable options by allowing you to compare prices across a wide range of hotels. You can filter results by budget, check out seasonal discounts, and take advantage of exclusive offers. Booking early or keeping an eye on special promotions can also help you secure the lowest rates in your desired city.
                  </p>
                </Accordion>
                <Accordion title="How to find the best hotels near me?">
                  <p>
                    Finding hotels near your location is easy with RROOMS. By enabling location access or simply searching for “hotels near me,” the app shows you nearby options that match your preferences. You can refine the results using filters like amenities, star rating, or guest reviews to make sure you book a stay that perfectly suits your needs.
                  </p>
                </Accordion>
                <Accordion title="Where can I find current deals and offers of RROOMS?">
                  <p>
                    All the latest deals and offers are regularly updated on the RROOMS app and website. You can check the dedicated offers section, explore banners for ongoing promotions, or subscribe to our updates to never miss out on discounts. Exclusive app-only offers and social media promotions also provide a great way to save on bookings.
                  </p>
                </Accordion>
                <Accordion title="What are the most important things when choosing a hotel?">
                  <p>
                    When choosing a hotel, it’s important to consider factors like location, budget, amenities, and safety. Look for properties that are close to the areas you want to visit, compare prices to get the best value, and ensure the hotel provides the facilities you require. Reading guest reviews and checking ratings can also help you make an informed decision.
                  </p>
                </Accordion>
                <Accordion title="How far in advance should I book a hotel?">
                  <p>
                    The best time to book a hotel depends on the season and destination. For busy travel periods and popular tourist spots, it’s advisable to book at least two to three months in advance. During regular travel periods, two to four weeks is usually enough. While last-minute deals may sometimes be available, booking early often gives you better rates and more choices.
                  </p>
                </Accordion>
                <Accordion title="Why should I book a hotel with RROOMS?">
                  <p>
                    RROOMS offers a wide selection of verified hotels with genuine customer reviews, making it easier for you to choose with confidence. The platform ensures a smooth booking process, secure payment options, and 24/7 customer support. With exclusive deals, flexible cancellation policies, and loyalty benefits, RROOMS provides a complete and reliable booking experience.
                  </p>
                </Accordion>
                <Accordion title="How can I find some of the best deals on hotels?">
                  <p>
                    You can discover the best hotel deals on RROOMS by booking during off-peak travel periods, using promo codes, and keeping track of seasonal sales. Comparing multiple properties and room types will also help you find a stay that fits your budget. Downloading the RROOMS app and joining loyalty programs ensures access to additional offers and exclusive discounts.
                  </p>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home